import { createAsyncThunk } from "@reduxjs/toolkit";
import CatalogImportApiService, {
  CatalogImportApiError,
  toFriendlyCatalogImportError,
} from "../apiServices/CatalogImportApiService";
import {
  CatalogBulkActionResult,
  CatalogCropResult,
  CatalogDraft,
  CatalogDraftBulkActionRq,
  CatalogDraftListQuery,
  CatalogDraftPage,
  CatalogImport,
  CatalogImportConfig,
  CatalogImportListQuery,
  CatalogImportRejection,
  CatalogPage,
  CatalogSectionMapRq,
  CreateCatalogCropRq,
  CreateCatalogImportResult,
  CreateCatalogImportRq,
  MergeCatalogDraftsRq,
  RerunCatalogPageRq,
  SplitCatalogDraftRq,
  UpdateCatalogDraftRq,
} from "../../interfaces/CatalogImportObject";
import {
  CATALOG_IMPORT_CLIENT_ERROR,
  isRetryableCatalogImportCode,
} from "../helpers/CatalogImportErrors";

/**
 * Async thunks for the catalogue PDF import.
 *
 * Chain: api service -> unwrapEnvelope / CatalogImportApiError -> converter ->
 * thunk -> slice (dataManagement/uiDb/CatalogImportDb.ts).
 *
 * Every thunk rejects with a CatalogImportRejection (friendly `message` plus
 * the error `code`, HTTP status and `existingImportId`), so a component's
 * `.unwrap()` catch can branch on `code` — see isCatalogImportRejection. The
 * slice's errorMap keeps `message`; errorCodeMap keeps `code`.
 *
 * Retry: only GET thunks retry (inside the api service). The POST / PATCH / PUT
 * thunks never repeat themselves. Callers that poll pass `silent: true`, which
 * keeps loading / error flags untouched and skips a request while the previous
 * silent one is still in flight.
 */

type ThunkConfig = { rejectValue: CatalogImportRejection };

/** Rejection from anything thrown by the api service. */
export function toCatalogRejection(error: unknown): CatalogImportRejection {
  if (error instanceof CatalogImportApiError) {
    return {
      message: toFriendlyCatalogImportError(error),
      code: error.errorCode,
      httpStatus: error.httpStatus,
      retryable: error.retryable,
      existingImportId: error.existingImportId,
      details: error.additionalDetails ?? null,
    };
  }
  const aborted = error instanceof DOMException && error.name === "AbortError";
  return {
    message: toFriendlyCatalogImportError(error),
    code: aborted ? CATALOG_IMPORT_CLIENT_ERROR.ABORTED : "CLIENT_ERROR",
    httpStatus: 0,
    retryable: isRetryableCatalogImportCode(aborted ? CATALOG_IMPORT_CLIENT_ERROR.ABORTED : "CLIENT_ERROR"),
    existingImportId: null,
    details: null,
  };
}

/** Normalises whatever `.unwrap()` threw into a CatalogImportRejection. */
export function asCatalogRejection(error: unknown): CatalogImportRejection {
  if (
    error &&
    typeof error === "object" &&
    typeof (error as CatalogImportRejection).message === "string" &&
    typeof (error as CatalogImportRejection).code === "string" &&
    "httpStatus" in (error as object)
  ) {
    return error as CatalogImportRejection;
  }
  return toCatalogRejection(error);
}

/** Silent (polling) requests in flight, so a slow response never stacks a second one on top. */
const silentInFlight = new Set<string>();

async function runSilently<T>(key: string, silent: boolean | undefined, run: () => Promise<T>): Promise<T> {
  if (!silent) return run();
  silentInFlight.add(key);
  try {
    return await run();
  } finally {
    silentInFlight.delete(key);
  }
}

// ── Config and list ──────────────────────────────────────────────────────────

/** Limits and remaining pages today. Also tells the UI whether the feature is on. */
export const fetchCatalogImportConfig = createAsyncThunk<CatalogImportConfig, void, ThunkConfig>(
  "catalogImport/fetchConfig",
  async (_arg, { rejectWithValue }) => {
    try {
      return await CatalogImportApiService.getConfig();
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  }
);

/** First page for a filter. Replaces the list (see the slice for the race guard). */
export const fetchCatalogImports = createAsyncThunk<CatalogImport[], CatalogImportListQuery, ThunkConfig>(
  "catalogImport/fetchImports",
  async (query, { rejectWithValue }) => {
    try {
      return await runSilently("list", query.silent, () => CatalogImportApiService.listImports(query));
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  },
  { condition: (query) => !(query.silent && silentInFlight.has("list")) }
);

/** The next page, appended. `before` is the last loaded row's `createdAt`. */
export const fetchMoreCatalogImports = createAsyncThunk<CatalogImport[], CatalogImportListQuery, ThunkConfig>(
  "catalogImport/fetchMoreImports",
  async (query, { rejectWithValue }) => {
    try {
      return await CatalogImportApiService.listImports(query);
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  }
);

/** One import with section maps and page summaries. `silent` for polling. */
export const fetchCatalogImport = createAsyncThunk<
  CatalogImport,
  { importId: string; silent?: boolean },
  ThunkConfig
>(
  "catalogImport/fetchImport",
  async ({ importId, silent }, { rejectWithValue }) => {
    try {
      return await runSilently(`detail_${importId}`, silent, () => CatalogImportApiService.getImport(importId));
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  },
  { condition: ({ importId, silent }) => !(silent && silentInFlight.has(`detail_${importId}`)) }
);

// ── Upload flow (create -> signed PUT -> start) ──────────────────────────────

/**
 * Creates the import (AWAITING_UPLOAD). NOT safe to repeat blindly: a second
 * create makes a second import — the upload hook only calls it once per file
 * and resumes via requestCatalogUploadUrl afterwards.
 */
export const createCatalogImport = createAsyncThunk<CreateCatalogImportResult, CreateCatalogImportRq, ThunkConfig>(
  "catalogImport/create",
  async (form, { rejectWithValue }) => {
    try {
      return await CatalogImportApiService.createImport(form);
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  }
);

/** Fresh signed upload URL while AWAITING_UPLOAD (expired, retry, resume). */
export const requestCatalogUploadUrl = createAsyncThunk<CreateCatalogImportResult, string, ThunkConfig>(
  "catalogImport/uploadUrl",
  async (importId, { rejectWithValue }) => {
    try {
      return await CatalogImportApiService.requestUploadUrl(importId);
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  }
);

/** After the PDF is uploaded: the server checks the object, then QUEUED. */
export const startCatalogImport = createAsyncThunk<CatalogImport, string, ThunkConfig>(
  "catalogImport/start",
  async (importId, { rejectWithValue }) => {
    try {
      return await CatalogImportApiService.startImport(importId);
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  }
);

// ── Import actions ───────────────────────────────────────────────────────────

/** Cancels an import that has not reached review (also "Discard" of an unfinished upload). */
export const cancelCatalogImport = createAsyncThunk<CatalogImport, string, ThunkConfig>(
  "catalogImport/cancel",
  async (importId, { rejectWithValue }) => {
    try {
      return await CatalogImportApiService.cancelImport(importId);
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  }
);

/** Re-queues FAILED pages (re-render + extract). */
export const retryCatalogFailedPages = createAsyncThunk<CatalogImport, string, ThunkConfig>(
  "catalogImport/retryFailedPages",
  async (importId, { rejectWithValue }) => {
    try {
      return await CatalogImportApiService.retryFailedPages(importId);
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  }
);

/** Rejects what is left and archives the import. */
export const archiveCatalogImport = createAsyncThunk<CatalogImport, string, ThunkConfig>(
  "catalogImport/archive",
  async (importId, { rejectWithValue }) => {
    try {
      return await CatalogImportApiService.archiveImport(importId);
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  }
);

/** Creates the approved products (all, or only `draftIds`). Then poll the import until COMPLETED. */
export const commitCatalogImport = createAsyncThunk<
  CatalogImport,
  { importId: string; draftIds?: string[] },
  ThunkConfig
>("catalogImport/commit", async ({ importId, draftIds }, { rejectWithValue }) => {
  try {
    return await CatalogImportApiService.commitImport(importId, draftIds);
  } catch (error) {
    return rejectWithValue(toCatalogRejection(error));
  }
});

/** Maps PDF series to business sections (or creates sections). Returns the refreshed import. */
export const saveCatalogSectionMaps = createAsyncThunk<
  CatalogImport,
  { importId: string; maps: CatalogSectionMapRq[] },
  ThunkConfig
>("catalogImport/sectionMaps", async ({ importId, maps }, { rejectWithValue }) => {
  try {
    return await CatalogImportApiService.putSectionMaps(importId, maps);
  } catch (error) {
    return rejectWithValue(toCatalogRejection(error));
  }
});

// ── Review ───────────────────────────────────────────────────────────────────

/** One page (signed image URLs, regions, crops, drafts). `silent` for the quiet refetch after a 403 on an image. */
export const fetchCatalogPage = createAsyncThunk<
  CatalogPage,
  { importId: string; pageNo: number; silent?: boolean },
  ThunkConfig
>(
  "catalogImport/fetchPage",
  async ({ importId, pageNo, silent }, { rejectWithValue }) => {
    try {
      return await runSilently(`page_${importId}_${pageNo}`, silent, () =>
        CatalogImportApiService.getPage(importId, pageNo)
      );
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  },
  { condition: ({ importId, pageNo, silent }) => !(silent && silentInFlight.has(`page_${importId}_${pageNo}`)) }
);

/** A page of drafts for the product list. `cursor` appends (the slice dedupes). */
export const fetchCatalogDrafts = createAsyncThunk<CatalogDraftPage, CatalogDraftListQuery, ThunkConfig>(
  "catalogImport/fetchDrafts",
  async (query, { rejectWithValue }) => {
    try {
      return await CatalogImportApiService.getDrafts(query);
    } catch (error) {
      return rejectWithValue(toCatalogRejection(error));
    }
  }
);

/**
 * Edits one draft. Debounce in the component, not here. A stale `version`
 * rejects with code 16222 (DRAFT_VERSION_CONFLICT). Never retried automatically.
 */
export const patchCatalogDraft = createAsyncThunk<
  CatalogDraft,
  { importId: string; draftId: string; form: UpdateCatalogDraftRq },
  ThunkConfig
>("catalogImport/patchDraft", async ({ importId, draftId, form }, { rejectWithValue }) => {
  try {
    return await CatalogImportApiService.patchDraft(importId, draftId, form);
  } catch (error) {
    return rejectWithValue(toCatalogRejection(error));
  }
});

/**
 * Approve / reject / restore / unapprove by ids, page or "all without flags".
 * The slice applies the resulting statuses to cached drafts (minus `skipped`)
 * and refreshes the import's counters from `progress`.
 */
export const bulkCatalogDraftAction = createAsyncThunk<
  CatalogBulkActionResult,
  { importId: string; form: CatalogDraftBulkActionRq },
  ThunkConfig
>("catalogImport/bulkAction", async ({ importId, form }, { rejectWithValue }) => {
  try {
    return await CatalogImportApiService.bulkDraftAction(importId, form);
  } catch (error) {
    return rejectWithValue(toCatalogRejection(error));
  }
});

/** Combines drafts into one product with variants. Returns the resulting drafts. */
export const mergeCatalogDrafts = createAsyncThunk<
  CatalogDraft[],
  { importId: string; form: MergeCatalogDraftsRq },
  ThunkConfig
>("catalogImport/mergeDrafts", async ({ importId, form }, { rejectWithValue }) => {
  try {
    return await CatalogImportApiService.mergeDrafts(importId, form);
  } catch (error) {
    return rejectWithValue(toCatalogRejection(error));
  }
});

/** Splits variants of a draft into products of their own. Returns the resulting drafts. */
export const splitCatalogDraft = createAsyncThunk<
  CatalogDraft[],
  { importId: string; draftId: string; form: SplitCatalogDraftRq },
  ThunkConfig
>("catalogImport/splitDraft", async ({ importId, draftId, form }, { rejectWithValue }) => {
  try {
    return await CatalogImportApiService.splitDraft(importId, draftId, form);
  } catch (error) {
    return rejectWithValue(toCatalogRejection(error));
  }
});

/** A crop drawn by hand (box in fractions of the page); optionally attached to / creating a draft. */
export const createCatalogCrop = createAsyncThunk<
  CatalogCropResult,
  { importId: string; pageNo: number; form: CreateCatalogCropRq },
  ThunkConfig
>("catalogImport/createCrop", async ({ importId, pageNo, form }, { rejectWithValue }) => {
  try {
    return await CatalogImportApiService.createCrop(importId, pageNo, form);
  } catch (error) {
    return rejectWithValue(toCatalogRejection(error));
  }
});

/** Reads one page again with an extra instruction. Returns the refreshed page. */
export const rerunCatalogPage = createAsyncThunk<
  CatalogPage,
  { importId: string; pageNo: number; form: RerunCatalogPageRq },
  ThunkConfig
>("catalogImport/rerunPage", async ({ importId, pageNo, form }, { rejectWithValue }) => {
  try {
    return await CatalogImportApiService.rerunPage(importId, pageNo, form);
  } catch (error) {
    return rejectWithValue(toCatalogRejection(error));
  }
});
